import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { TaskType } from '@/services/tasks/task.types';
import { useUpdateTasks } from '@/services/tasks/useTasks';
import { FunctionComponent, useState } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminDeactivateTaskCardProps {
  task: TaskType;
}

const config = [
  // false = inactive
  {
    title: adminStrings.tasksSection.deactivateReactivate.reactivate,
    icon: IoRefresh,
    info: adminStrings.tasksSection.deactivateReactivate.reactivateInfo,
    buttonText: adminStrings.common.reactivate,
    confirmTitle: adminStrings.tasksSection.deactivateReactivate.confirmReactivateTitle,
    confirmBody: adminStrings.tasksSection.deactivateReactivate.confirmReactivateBody,
    successMessage: adminStrings.tasksSection.deactivateReactivate.successReactivate,
    errorMessage: adminStrings.tasksSection.deactivateReactivate.errorReactivate
  },
  // true = active
  {
    title: adminStrings.tasksSection.deactivateReactivate.deactivate,
    icon: IoClose,
    info: adminStrings.tasksSection.deactivateReactivate.deactivateInfo,
    buttonText: adminStrings.common.deactivate,
    confirmTitle: adminStrings.tasksSection.deactivateReactivate.confirmTitle,
    confirmBody: adminStrings.tasksSection.deactivateReactivate.confirmDeactivateBody,
    successMessage: adminStrings.tasksSection.deactivateReactivate.successDeactivate,
    errorMessage: adminStrings.tasksSection.deactivateReactivate.errorDeactivate
  }
] as const;

const AdminDeactivateReactivateTaskCard: FunctionComponent<AdminDeactivateTaskCardProps> = ({ task }) => {
  const { title, icon, info, buttonText, confirmTitle, confirmBody, successMessage, errorMessage } =
    config[+task.isActive];

  const { fetching, update } = useUpdateTasks();
  const navigate = useNavigate();

  const onSubmit = async () => {
    const result = await update([{ ...task, isActive: !task.isActive }]);

    await handleMutationResult(result, {
      successMessage,
      errorMessage,
      onSuccess: () => navigate('/admin/tasks')
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <TSTitleCard title={title(task.name)} icon={icon} className="w-full">
      <div className="flex flex-row items-center space-x-3">
        <Button onClick={() => setIsModalOpen(true)} loading={fetching} danger tint>
          {buttonText}
        </Button>
        <p>{info}</p>
      </div>

      <ConfirmDialog
        onConfirm={onSubmit}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={confirmTitle(task.name)}
        confirmLabel={buttonText}
        cancelLabel={adminStrings.common.cancel}
      >
        {confirmBody}
      </ConfirmDialog>
    </TSTitleCard>
  );
};

export default AdminDeactivateReactivateTaskCard;
